import { NOT_SET } from '@/app/lib/analytics/constants'
import { mapProductToAnalyticsItemBase } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { ContextualProductListAnalytics, EcommerceItemBase } from '@/app/lib/analytics/types'
import type { ProductApiItem } from '@/app/lib/api/types-api'

/**
 * `module_position` de un módulo CMS general: `position` del payload (orden entre
 * los módulos de la página, `"2"` en los contratos reales). Sin dato fiable →
 * `not_set` (no se inventa una posición).
 */
export function resolveModulePosition(content: unknown): string {
  if (typeof content !== 'object' || content === null) return NOT_SET
  const { position } = content as { position?: unknown }
  if (typeof position === 'number' && Number.isFinite(position)) return String(position)
  if (typeof position === 'string' && position.trim() !== '') return position.trim()
  return NOT_SET
}

/** Un item de `contents[]` que no es un producto (`type: "Página"` de `Productos`). */
type MaybeProduct =
  | (ProductApiItem & { readonly type?: string })
  | { readonly id: string | number; readonly type: 'Página' }

/**
 * `items[]` alineados con las cards ya renderizadas. Los mapeadores de cards
 * descartan elementos (sin título, sin enlace) y recortan, así que NO se mapea
 * `contents` por posición: se resuelve por `id` contra los productos crudos. Una
 * card sin producto (p. ej. un item `"Página"`, que no es un evento) queda `null`
 * → sin instrumentar, en vez de desalinear el resto.
 */
export function toModuleAnalyticsItems(
  cards: readonly { readonly id: string | number }[],
  contents: readonly MaybeProduct[],
): (EcommerceItemBase | null)[] {
  const byId = new Map<string, ProductApiItem>()
  for (const product of contents) {
    if (product.type === 'Página' || !('title' in product)) continue
    byId.set(String(product.id), product)
  }
  return cards.map((card) => {
    const product = byId.get(String(card.id))
    return product ? mapProductToAnalyticsItemBase(product) : null
  })
}

interface ModuleListIdentity {
  readonly moduleName: string
  readonly moduleType: string
  readonly itemListName: string
}

/** Config de analítica de un listado de producto de un módulo CMS general. */
export function buildModuleListAnalytics(
  identity: ModuleListIdentity,
  content: unknown,
  cards: readonly { readonly id: string | number }[],
  contents: readonly MaybeProduct[],
): ContextualProductListAnalytics {
  return {
    ...identity,
    modulePosition: resolveModulePosition(content),
    items: toModuleAnalyticsItems(cards, contents),
  }
}
